import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAccountsContext } from '@/features/accounts/context/AccountsContext';
import { toast } from 'react-hot-toast';
import { markFunnelStage } from '@/shared/analytics';
import { useIsMobile, useModalState } from '@/shared/hooks';
import EmptyState from './EmptyState';
import CreationModal from '@/features/accounts/creation/components/CreationModal';
import AccountsListHeader from './AccountsListHeader';
import AccountsListLoadingState from './AccountsListLoadingState';
import TableView from '@/features/accounts/table/components/TableView';
import ListView from './ListView';

interface Props {
  onNeedHelp?: () => void;
}

export default function AccountList({ onNeedHelp }: Props) {
  const { accounts, isLoading, error } = useAccountsContext();
  const { isOpen: isCreateOpen, open: openCreate, close: closeCreate } = useModalState();
  const intl = useIntl();
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');

  const isMobile = useIsMobile();
  const showViewToggle = !isLoading && accounts.length > 1 && !isMobile;
  const effectiveViewMode = isMobile ? 'card' : (accounts.length < 2 ? 'card' : viewMode);
  const isTableView = effectiveViewMode === 'table';

  if (isLoading) {
    return <AccountsListLoadingState />;
  }

  const openCreateWithTracking = () => {
    markFunnelStage('add_account_cta_clicked', 'add_account_cta_clicked', { source: 'empty_state' });
    openCreate();
  };

  const notifyScheduleActivated = () => {
    toast.success(intl.formatMessage({ id: 'schedule.activate.toast' }));
  };

  if (error) {
    return (
      <div className="font-sans">
        <div className="text-center p-8 text-red-600">
          <FormattedMessage id="accounts.list.error" values={{ message: error.message }} />
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <AccountsListHeader
        showViewToggle={showViewToggle}
        isTableView={isTableView}
        onSelectView={setViewMode}
      />
      {accounts.length === 0 ? (
        <EmptyState onCreateClick={onNeedHelp ?? openCreateWithTracking} />
      ) : (
        <>
          {effectiveViewMode === 'table' ? (
            <TableView
              accounts={accounts}
              onActivated={notifyScheduleActivated}
              onCreate={openCreate}
            />
          ) : (
            <ListView
              accounts={accounts}
              onActivated={notifyScheduleActivated}
              onCreate={openCreate}
            />
          )}
        </>
      )}
      <CreationModal
        isOpen={isCreateOpen}
        onClose={closeCreate}
        onActivated={notifyScheduleActivated}
      />
    </div>
  );
}
